import { __decorate } from "tslib";
import { AutoEncoder, field, StringDecoder, EnumDecoder, ArrayDecoder, PatchMap } from '@simonbackx/simple-encoding';
import { AccessRight, AccessRightHelper } from './AccessRight.js';
import { PermissionLevel, getPermissionLevelNumber } from './PermissionLevel.js';
import { getPermissionResourceTypeName } from './PermissionsResourceType.js';
import { Formatter } from '@stamhoofd/utility';
/**
 * More granular access rights to specific things in the system
 */
export class ResourcePermissions extends AutoEncoder {
    /**
     * This is a cache so we can display the role description without fetching all resources
     */
    resourceName = '';
    /**
     * Setting it to full gives all possible permissions for the resource
     * Read/Write depends on resource
     */
    level = PermissionLevel.None;
    /**
     * More granular permissions related to this resource
     */
    accessRights = [];
    getDiffName() {
        return this.resourceName;
    }
    hasAccess(level) {
        return getPermissionLevelNumber(this.level) >= getPermissionLevelNumber(level);
    }
    hasAccessRight(right) {
        const gl = AccessRightHelper.autoGrantRightForLevel(right);
        return (gl && this.hasAccess(gl)) || this.accessRights.includes(right);
    }
    createInsertPatch(type, resourceId, roleOrPermissions) {
        const patch = roleOrPermissions.static.patch({});
        // First check if we need to insert the type
        if (roleOrPermissions.resources.get(type)) {
            // We need to patch
            const p = new PatchMap();
            p.set(resourceId, this);
            patch.resources.set(type, p);
        }
        else {
            // No resources with this type yet
            const p = new Map();
            p.set(resourceId, this);
            patch.resources.set(type, p);
        }
        return patch;
    }
    merge(other) {
        const p = new ResourcePermissions();
        p.level = this.level;
        p.accessRights = this.accessRights.slice();
        if (getPermissionLevelNumber(other.level) > getPermissionLevelNumber(p.level)) {
            p.level = other.level;
        }
        for (const right of other.accessRights) {
            if (!p.accessRights.includes(right)) {
                p.accessRights.push(right);
            }
        }
        return p;
    }
    removeAccessRights(rights) {
        this.accessRights = this.accessRights.filter(r => !rights.includes(r));
    }
    get isEmpty() {
        return this.level === PermissionLevel.None && this.accessRights.length === 0;
    }
    static getMapDescription(map) {
        const stack = [];
        const resourceDescription = (resource) => {
            const accessRights = resource.accessRights.map(a => AccessRightHelper.getDescription(a));
            if (resource.level === PermissionLevel.None) {
                return Formatter.joinLast(accessRights, ', ', ' ' + $t(`%M1`) + ' ');
            }
            let prefix = $t(`%nD`);
            if (resource.level === PermissionLevel.Write) {
                prefix = $t(`%Rw`);
            }
            else if (resource.level === PermissionLevel.Full) {
                prefix = $t(`%m9`);
            }
            return prefix + (accessRights.length > 0 ? ' ' + $t(`%nE`) + ' ' + Formatter.joinLast(accessRights, ', ', ' ' + $t(`%M1`) + ' ') : '');
        };
        for (const [type, resources] of map) {
            const all = resources.get('');
            let allDescription = null;
            if (all && !all.isEmpty) {
                const accessRights = all.accessRights.map(a => AccessRightHelper.getDescription(a));
                if (all.level === PermissionLevel.None) {
                    stack.push($t(`%nF`) + ' ' + getPermissionResourceTypeName(type, true) + ': ' + Formatter.joinLast(accessRights, ', ', ' ' + $t(`%M1`) + ' '));
                }
                let prefix = $t(`%nF`) + ' ';
                let suffix = ' ' + $t(`%nD`);
                if (all.level === PermissionLevel.Write) {
                    suffix = ' ' + $t(`%Rw`);
                }
                else if (all.level === PermissionLevel.Full) {
                    prefix = $t(`%nG`) + ' ';
                    suffix = '';
                }
                stack.push(prefix + getPermissionResourceTypeName(type, true) + suffix + (accessRights.length > 0 ? ' ' + $t(`%nE`) + ' ' + Formatter.joinLast(accessRights, ', ', ' ' + $t(`%M1`) + ' ') : ''));
                allDescription = resourceDescription(all);
            }
            const countsPer = new Map();
            for (const resource of resources.values()) {
                if (!resource.isEmpty) {
                    const description = resourceDescription(resource);
                    if (description === allDescription) {
                        continue;
                    }
                    if (!countsPer.has(description)) {
                        countsPer.set(description, { count: 0, firstName: resource.resourceName ?? null });
                    }
                    countsPer.get(description).count += 1;
                }
            }
            for (const [description, { count, firstName }] of countsPer) {
                stack.push((count === 1 && firstName ? firstName : (count + ' ' + getPermissionResourceTypeName(type, count > 1))) + ' (' + description + ')');
            }
        }
        return stack;
    }
}
__decorate([
    field({ decoder: StringDecoder, field: 'n' })
], ResourcePermissions.prototype, "resourceName", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PermissionLevel), field: 'l' })
], ResourcePermissions.prototype, "level", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(AccessRight)), field: 'r' })
], ResourcePermissions.prototype, "accessRights", void 0);
//# sourceMappingURL=ResourcePermissions.js.map